import { useAuth } from '../context/AuthContext';
import { 
  formatDateInBusinessTZ, 
  formatDateOnlyInBusinessTZ,
  formatTimeOnlyInBusinessTZ,
  timeAgo,
  detectBrowserTimeZone,
  convertDateBetweenTimeZones,
  getTimeZoneOffsetDiff,
  getTimeZoneDifferenceText,
  getTimeZoneLabel,
  isSameDayInTimeZone,
  formatDateWithoutTZ
} from '../utils/dates';
import { isTimestampLike, type TimestampLike } from '../types/dateLike';
import { useState, useEffect } from 'react';

// Opciones para el formato de fecha
// 'dateTime' = fecha+hora completa (alias de 'full'); 'short' = solo fecha corta (alias de 'dateOnly');
// 'relativeShort'/'relativeOrShort' = tiempo relativo ("hace X") (alias de 'timeAgo').
export type DateFormatType =
  | 'full'
  | 'dateOnly'
  | 'timeOnly'
  | 'timeAgo'
  | 'birthday'
  | 'dateTime'
  | 'short'
  | 'relativeShort'
  | 'relativeOrShort';

// Opciones para la configuración del hook
export interface DateFormattingOptions {
  showBothTimeZones?: boolean; // Mostrar tanto la zona del negocio como la del usuario
  preferredTimeZone?: string;  // Zona horaria preferida por el usuario (si no usa la del negocio)
}

/**
 * Hook personalizado para manejar el formato de fechas y zonas horarias
 * 
 * Proporciona un conjunto de utilidades para formatear fechas considerando
 * las zonas horarias del negocio y del usuario.
 * 
 * @param options - Opciones de configuración
 * @returns Objeto con funciones y datos para el manejo de fechas
 */
export const useDateFormatting = (options: DateFormattingOptions = {}) => {
  const { currentUser } = useAuth();
  
  // Obtener la zona horaria del negocio desde el usuario actual
  const businessTimeZone = currentUser?.timeZone || 'America/Mexico_City';
  
  // Zona horaria preferida por el usuario (por defecto, la detectada en el navegador)
  const [userTimeZone, setUserTimeZone] = useState<string>(
    options.preferredTimeZone || detectBrowserTimeZone()
  );
  
  // Si cambia la opción preferredTimeZone, actualizar el estado
  useEffect(() => {
    if (options.preferredTimeZone) {
      setUserTimeZone(options.preferredTimeZone);
    }
  }, [options.preferredTimeZone]);
  
  // Verificar si se deben mostrar ambas zonas horarias
  const showBothTimeZones = options.showBothTimeZones || false;
  
  // Calcular diferencia entre zonas horarias
  const timeZoneDifference = getTimeZoneOffsetDiff(businessTimeZone, userTimeZone);
  const isDifferentTimeZone = businessTimeZone !== userTimeZone;
  
  /**
   * Función principal para formatear fechas con múltiples opciones
   */
  const formatDate = (
    date: Date | TimestampLike | null | undefined,
    formatOptions: {
      type?: DateFormatType,
      useBusinessTZ?: boolean,
      customTZ?: string
    } = {}
  ): string => {
    const { 
      type = 'full', 
      useBusinessTZ = true,
      customTZ 
    } = formatOptions;
    
    // Determinar qué zona horaria utilizar
    const tzToUse = customTZ || (useBusinessTZ ? businessTimeZone : userTimeZone);
    
    switch(type) {
      case 'dateOnly':
      case 'short':
        return formatDateOnlyInBusinessTZ(date, tzToUse);
      case 'timeOnly':
        return formatTimeOnlyInBusinessTZ(date, tzToUse);
      case 'timeAgo':
      case 'relativeShort':
      case 'relativeOrShort':
        return timeAgo(isTimestampLike(date) ? date.toDate() : date);
      case 'birthday':
        // Para cumpleaños usamos formato sin zona horaria (son fechas absolutas)
        return formatDateWithoutTZ(date, {
          day: '2-digit',
          month: 'long'
        });
      case 'full':
      default:
        return formatDateInBusinessTZ(date, tzToUse);
    }
  };
  
  /**
   * Formatea una fecha mostrando ambas zonas horarias si es necesario
   */
  const formatDateWithZoneInfo = (
    date: Date | TimestampLike | null | undefined,
    formatOptions: {
      type?: DateFormatType,
      alwaysShowBoth?: boolean
    } = {}
  ): { text: string, secondary?: string, diff?: string } => {
    const { type = 'full', alwaysShowBoth = false } = formatOptions;
    
    // Si las zonas son iguales o no se pide mostrar ambas, solo mostrar una
    if (!isDifferentTimeZone || (!showBothTimeZones && !alwaysShowBoth)) {
      return {
        text: formatDate(date, { type, useBusinessTZ: true })
      };
    }
    
    // Mostrar ambas zonas horarias
    return {
      text: formatDate(date, { type, useBusinessTZ: true }),
      secondary: formatDate(date, { type, useBusinessTZ: false }),
      diff: getTimeZoneDifferenceText(businessTimeZone, userTimeZone)
    };
  };
  
  /**
   * Convierte una fecha de la zona horaria del negocio a la del usuario
   */
  const convertBusinessToUserTZ = (date: Date | TimestampLike | null): Date | null => {
    return convertDateBetweenTimeZones(date, businessTimeZone, userTimeZone);
  };
  
  /**
   * Convierte una fecha de la zona horaria del usuario a la del negocio
   */
  const convertUserToBusinessTZ = (date: Date | TimestampLike | null): Date | null => {
    return convertDateBetweenTimeZones(date, userTimeZone, businessTimeZone);
  };
  
  /**
   * Comprueba si dos fechas son del mismo día en la zona horaria del negocio
   */
  const isSameBusinessDay = (
    date1: Date | TimestampLike | null | undefined,
    date2: Date | TimestampLike | null | undefined
  ): boolean => {
    return isSameDayInTimeZone(date1, date2, businessTimeZone);
  };
  
  /**
   * Comprueba si una fecha es del día actual en la zona horaria del negocio
   */
  const isToday = (date: Date | TimestampLike | null | undefined): boolean => {
    return isSameDayInTimeZone(date, new Date(), businessTimeZone);
  };
  
  /**
   * Retorna la fecha y hora actual en la zona horaria del negocio
   */
  const getCurrentBusinessDateTime = (): Date => {
    // Crear fecha actual y convertirla a la zona del negocio
    return convertDateBetweenTimeZones(new Date(), userTimeZone, businessTimeZone) || new Date();
  };
  
  /**
   * Retorna la fecha actual (sin hora) en la zona horaria del negocio
   */
  const getCurrentBusinessDate = (): Date => {
    const now = getCurrentBusinessDateTime();
    now.setHours(0, 0, 0, 0);
    return now;
  };
  
  // Objetos con información de zonas horarias
  const timeZoneInfo = {
    business: {
      zone: businessTimeZone,
      label: getTimeZoneLabel(businessTimeZone)
    },
    user: {
      zone: userTimeZone,
      label: getTimeZoneLabel(userTimeZone)
    },
    difference: timeZoneDifference,
    isDifferent: isDifferentTimeZone,
    differenceText: getTimeZoneDifferenceText(businessTimeZone, userTimeZone)
  };
  
  // Retornar todas las funciones y datos útiles
  return {
    // Funciones principales
    formatDate,
    formatDateWithZoneInfo,
    
    // Ayudantes para casos específicos
    formatFull: (date: Date | TimestampLike | null | undefined) => 
      formatDate(date, { type: 'full' }),
    formatDateOnly: (date: Date | TimestampLike | null | undefined) => 
      formatDate(date, { type: 'dateOnly' }),
    formatTimeOnly: (date: Date | TimestampLike | null | undefined) => 
      formatDate(date, { type: 'timeOnly' }),
    formatTimeAgo: (date: Date | TimestampLike | null | undefined) => 
      formatDate(date, { type: 'timeAgo' }),
    formatBirthday: (date: Date | TimestampLike | null | undefined) => 
      formatDate(date, { type: 'birthday' }),
    
    // Conversiones entre zonas horarias
    convertBusinessToUserTZ,
    convertUserToBusinessTZ,
    
    // Utilidades de comparación
    isSameBusinessDay,
    isToday,

    // Fechas actuales
    getCurrentBusinessDateTime,
    getCurrentBusinessDate,
    
    // Información de zona horaria
    timeZoneInfo,
    
    // Zonas horarias
    businessTimeZone,
    userTimeZone,
    setUserTimeZone
  };
};

export default useDateFormatting;